import React, { useEffect, useState, useMemo, useRef } from "react";
import { Link, useNavigate } from "react-router-dom"; // ⬅️ useNavigate added

import Hero from "../components/Hero.jsx";
import CategoryBar from "../components/CategoryBar.jsx";
import Section from "../components/Section.jsx";
import EventCard from "../components/EventCard.jsx";
import TestimonialCard from "../components/TestimonialCard.jsx";
import AppCTA from "../components/AppCTA.jsx";
import Newsletter from "../components/Newsletter.jsx";
import PromoStrip from "../components/PromoStrip.jsx";
import CouponModal from "../components/CouponModal.jsx";

import {
  apiFeatured,
  apiTrending,
  apiWeekend,
  fetchEventsByCategory,
  fetchCities,
  testimonials,
} from "../api/events.js";

import FeaturedCarousel from "../components/FeaturedCarousel.jsx";
import { get } from "../lib/http.js";
import { API } from "../config/api.js";

/* ------------ Helpers ------------ */
const formatINR = (n) =>
  Number.isFinite(Number(n)) ? `₹${Number(n).toLocaleString("en-IN")}` : "";

const formatEndDateIST = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleString("en-IN", {
    timeZone: "Asia/Kolkata",
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const isWithinValidity = (c) => {
  const now = Date.now();
  const from = c?.validFrom ? Date.parse(c.validFrom) : -Infinity;
  const to = c?.validTo ? Date.parse(c.validTo) : Infinity;
  return now >= from && now <= to;
};

const hasUsageLeft = (c) =>
  typeof c?.usageLimit === "number"
    ? (c.usedCount || 0) < c.usageLimit
    : true;

// empty applicableEvents = all events
const isCouponApplicableGlobally = (c) =>
  !Array.isArray(c?.applicableEvents) || c.applicableEvents.length === 0;

// const couponToOffer = (coupon) => {
//   const isPct = coupon.discountType === "percentage";
//   const discountText = isPct
//     ? `Flat ${coupon.value}% OFF`
//     : `Save ${formatINR(coupon.value)}`;
//   const minText = coupon.minPurchaseAmount
//     ? `• Min ${formatINR(coupon.minPurchaseAmount)}`
//     : "";
//   const endText = coupon.validTo ? `• Ends ${formatEndDateIST(coupon.validTo)}` : "";

//   return {
//     id: coupon._id,
//     img: coupon.imageUrl || "", // PromoStrip shows gradient fallback
//     alt: coupon.description || "Offer",
//     title: coupon.description || "Special Offer",
//     subtitle: `${discountText} ${minText} ${endText}`.trim(),
//     couponCode: coupon.code,
//     href: "#",
//     external: false,
//   };
// };

const couponToOffer = (coupon) => {
  const isPct = coupon.discountType === "percentage";
  const discountText = isPct
    ? `Flat ${coupon.value}% OFF`
    : `Save ${formatINR(coupon.value)}`;
  const minText = coupon.minPurchaseAmount
    ? `• Min ${formatINR(coupon.minPurchaseAmount)}`
    : "";
  const endText = coupon.validTo ? `• Ends ${formatEndDateIST(coupon.validTo)}` : "";

  // If exactly one event targeted, deep-link to checkout
  const eventId =
    Array.isArray(coupon.applicableEvents) && coupon.applicableEvents.length === 1
      ? coupon.applicableEvents[0]
      : null;

  return {
    id: coupon._id,
    img: coupon.imageUrl || "",
    alt: coupon.description || "Offer",
    title: coupon.description || "Special Offer",
    subtitle: `${discountText} ${minText} ${endText}`.trim(),
    couponCode: coupon.code,
    href: eventId ? `/checkout/${eventId}?coupon=${encodeURIComponent(coupon.code)}` : "#",
    external: false,
    // ✅ propagate isShow (default true)
    isShow: coupon.isShow !== false,
  };
};

/* --------------------------------- */

export default function HomePage() {
  const nav = useNavigate(); // ⬅️ now defined
  const weekendRef = useRef(null);
  const [cat, setCat] = useState("all");
  const [featured, setFeatured] = useState([]);
  const [trending, setTrending] = useState([]);
  const [weekend, setWeekend] = useState([]);
  const [city, setCity] = useState("all");
  const [loadingFeatured, setLoadingFeatured] = useState(true);
  const FEATURED_LIMIT = 4;

  const [coupons, setCoupons] = useState([]);
  const [citiesOpt, setCitiesOpt] = useState([{ name: "All", value: "all", img: "" }]);
  const [citiesLoading, setCitiesLoading] = useState(true);

  const COUPON_MODAL_KEY = "coupon_modal_shown_v1";

  const [showCouponModal, setShowCouponModal] = useState(false);
  const [couponToShow, setCouponToShow] = useState(null);

  // derive login state from your stored user
  const [isLoggedIn, setIsLoggedIn] = useState(() => {
    try {
      const user = JSON.parse(localStorage.getItem("bb_user") || "null");
      return !!user?.token;
    } catch {
      return false;
    }
  });





  // Load discounts
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const token = JSON.parse(localStorage.getItem("bb_user") || "null")?.token; // ⬅️ safe parse
        const res = await get(`${API.coupon.getDiscount}`, {
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            "Content-Type": "application/json",
          },
        });
        if (!alive) return;
        const list = Array.isArray(res)
          ? res
          : Array.isArray(res?.discounts)
            ? res.discounts
            : Array.isArray(res?.discount)
              ? res.discount
              : Array.isArray(res?.data?.discounts)
                ? res.data.discounts
                : [];

        setCoupons(list || []);
      } catch (e) {
        console.error("load discounts error:", e);
        setCoupons([]);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  // Featured (respects category)
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setLoadingFeatured(true);
        const list =
          cat === "all"
            ? await apiFeatured(FEATURED_LIMIT)
            : await fetchEventsByCategory(cat, { limit: FEATURED_LIMIT });
        if (alive) setFeatured(list);
      } catch (e) {
        console.error("featured load error:", e);
        if (alive) setFeatured([]);
      } finally {
        if (alive) setLoadingFeatured(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [cat]);

  const promoOffers = useMemo(() => {
    if (!Array.isArray(coupons)) return [];
    const filtered = coupons.filter(
      (c) =>
        c?.isActive &&
        isWithinValidity(c) &&
        hasUsageLeft(c) &&
        c?.isShow !== false
    );
    const sorted = filtered.sort((a, b) => {
      const ta = a.validTo ? Date.parse(a.validTo) : Infinity;
      const tb = b.validTo ? Date.parse(b.validTo) : Infinity;
      return ta - tb;
    });
    return sorted.map(couponToOffer);
  }, [coupons]);

  // replace your existing useEffect(...) that decides showCouponModal with this:

useEffect(() => {
  if (!Array.isArray(promoOffers) || promoOffers.length === 0) {
    setShowCouponModal(false);
    setCouponToShow(null);
    return;
  }

  // NEW BEHAVIOR: show popup ONLY for visitors (NOT logged-in users)
  if (!isLoggedIn) {
    // small delay so it doesn't clash with initial render
    setTimeout(() => {
      setCouponToShow(promoOffers[0]);
      setShowCouponModal(true);
    }, 120);
    return;
  }

  // If user is logged in -> do NOT show popup at all
  setShowCouponModal(false);
  setCouponToShow(null);

  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [JSON.stringify(promoOffers), isLoggedIn]);


  // Independent sections
  useEffect(() => { apiWeekend().then(setWeekend); }, []);
  useEffect(() => { apiTrending(city, 4).then(setTrending); }, [city]);

  // Cities
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setCitiesLoading(true);
        const list = await fetchCities();
        if (!alive) return;
        setCitiesOpt([{ name: "All", value: "all", img: "" }, ...list]);
      } catch (e) {
        const fallback = [];
        setCitiesOpt(fallback);
      } finally {
        setCitiesLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);


  const handleApplyCodeFromBanner = async (code) => {
    try { localStorage.setItem("lastCoupon", code); } catch { }
    nav(`/featured?coupon=${encodeURIComponent(code)}`);
  };

  const getEventId = (ev) => ev?.id ?? ev?._id ?? ev?.eventId ?? ev?._id?.$oid;

  // Earliest schedule timestamp of an event (for date-wise sorting)
  const getEventStartTs = (ev) => {
    const raw = ev?.__raw || ev;
    const times = [];
    if (Array.isArray(raw?.showtimes)) {
      for (const s of raw.showtimes) {
        const t = new Date(s?.startTime).getTime();
        if (!Number.isNaN(t)) times.push(t);
      }
    }
    if (times.length) return Math.min(...times);
    const d = new Date(raw?.date).getTime();
    return Number.isNaN(d) ? Infinity : d;
  };


  return (
    <>
      <Hero />

      <CategoryBar value={cat} onChange={setCat} />

      <Section
        id="featured"
        title={cat === "all" ? "Featured Events" : `Events in ${cat}`}
        bgClass="bg-gradient-to-b from-amber-50/95 via-orange-50/40 to-white border-y border-amber-100/60"
        fullBleed
        right={
          featured.length >= 4 ? (
            <Link
              to="/featured"
              className="font-sans text-sm font-semibold text-amber-800 hover:text-amber-950 hover:underline"
            >
              View more
            </Link>
          ) : (
            ""
          )
        }
      >
        {loadingFeatured ? (
          <div className="flex items-center gap-3 rounded-xl border border-amber-100/80 bg-white/60 px-4 py-6 font-sans text-sm text-slate-600">
            <span className="inline-block h-5 w-5 animate-spin rounded-full border-2 border-amber-400 border-t-transparent" />
            Loading events…
          </div>
        ) : featured.length ? (
          <div className="grid grid-cols-1 gap-4 font-sans sm:grid-cols-2 sm:gap-5 lg:grid-cols-4">
            {[...featured]
              .sort((a, b) => getEventStartTs(a) - getEventStartTs(b))
              .slice(0, 4)
              .map((e, i) => (
                <EventCard key={`featured-${getEventId(e) ?? i}-${i}`} item={e} />
              ))}
          </div>
        ) : (
          <div className="rounded-xl border border-dashed border-amber-200/80 bg-white/70 px-4 py-8 text-center font-sans text-sm text-slate-600">
            No events found for this category.
          </div>
        )}
      </Section>

      {/* <Section
        id="trending"
        title={`Trending in ${city === "all" ? "All Cities" : city}`}
        right={
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <span className="text-sm text-brand-700">Change City</span>
              <select
                className="border rounded-md px-3 py-2 text-sm"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                disabled={citiesLoading}
              >
                {citiesOpt.map((c) => (
                  <option key={c.value} value={c.value}>{c.name}</option>
                ))}
              </select>
            </div>
            <Link to={`/trending?city=${encodeURIComponent(city)}`} className="text-sm text-brand-700 hover:underline">
              View All
            </Link>
          </div>
        }
      >
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {trending.map((e) => (
            <EventCard key={e.id} item={e} compact showBookButton={false} bodyBg="yellow" />
          ))}
        </div>
      </Section> */}

      {/* <Section
        id="trending"
        // title={`Trending in ${city === "all" ? "All Cities" : city}`}
        title={`Trending in Cities`}
        right={
          <div className="flex items-center gap-3">
            <div className="hidden md:flex items-center gap-2">
              <span className="text-sm text-brand-700">Change City</span>
              <select
                className="border rounded-md px-3 py-2 text-sm"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                disabled={citiesLoading}
              >
                {citiesOpt.map((c) => (
                  <option key={c.value} value={c.value}>{c.name}</option>
                ))}
              </select>
            </div>

            {
            //  <Link
            //   to={`/trending?city=${encodeURIComponent(city)}`}
            //   className="text-sm text-brand-700 hover:underline"
            // >
            //   View All
            // </Link> 
            }
          </div>
        }
      >
        <div className="mt-2 md:hidden">
          <div className="flex items-center gap-2">
            <span className="text-sm text-brand-700">Change City</span>
            <select
              className="border rounded-md px-3 py-2 text-sm"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              disabled={citiesLoading}
            >
              {citiesOpt.map((c) => (
                <option key={c.value} value={c.value}>{c.name}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-[20px]">
          {
          // {trending.map((e) => (
          //   <EventCard key={e.id} item={e} compact showBookButton={false} bodyBg="yellow" />
          // ))}
          }
          {trending.map((e) => (
            <Link key={getEventId(e)} to={`/events/${getEventId(e)}`} className="block">
              <EventCard item={e} compact showBookButton={false} bodyBg="yellow" />
            </Link>
          ))}
        </div>
      </Section> */}
      <FeaturedCarousel />
      {Array.isArray(weekend) && weekend.length > 0 && <Section id="weekend" title="Past Events" bgClass="bg-gradient-to-b from-amber-50/90 to-white border-y border-amber-100/50" fullBleed
      // right={<Link to="/weekend" className="text-sm text-brand-700 hover:underline">View All</Link>}
      >
        <div className="-mx-4 overflow-x-clip">
          <div className="no-scrollbar flex gap-4 overflow-x-auto scroll-smooth snap-x snap-mandatory px-4 pb-2 md:pr-12 lg:pr-16 overscroll-x-contain">


            {weekend.map((e, i) => {
              const id = getEventId(e) || i;
              return (
                <div key={`weekend-${id}-${i}`} className="shrink-0 snap-start w-[calc(100%_-_48px)] sm:w-[calc(100%_-_48px)] md:w-[calc((100%_-_48px_-_16px)/2)] lg:w-[calc((100%_-_64px_-_32px)/4)]">
                  <div className="block">
                    <EventCard item={{ ...e, id }} compact showBookButton={false} />
                  </div>
                </div>
              );
            })}

          </div>
        </div>
      </Section>}

      {/* <Section id="weekend" title="This Weekend" bgClass="bg-[#fff8e1]" fullBleed
        right={<Link to="/weekend" className="text-sm text-brand-700 hover:underline">View All</Link>}
      >
        <div className="relative">
          <button
            onClick={() => weekendRef.current.scrollBy({ left: -300, behavior: "smooth" })}
            className="absolute left-2 top-1/2 -translate-y-1/2 bg-white p-2 rounded-full shadow hidden md:block"
          >
            ◀
          </button>

          <div
            ref={weekendRef}
            className="no-scrollbar flex gap-4 overflow-x-auto scroll-smooth snap-x snap-mandatory px-4 pb-2 md:pr-12 lg:pr-16 overscroll-x-contain"
          >
            {weekend.map((e, i) => (
              <div key={e.id || i} className="shrink-0 snap-start w-[calc(100%_-_48px)] sm:w-[calc(100%_-_48px)] md:w-[calc((100%_-_48px_-_16px)/2)] lg:w-[calc((100%_-_64px_-_32px)/4)]">
                <Link to={`/events/${e.id}`} className="block">
                  <EventCard item={e} compact showBookButton={false} />
                </Link>
              </div>
            ))}
          </div>

          <button
            onClick={() => weekendRef.current.scrollBy({ left: 300, behavior: "smooth" })}
            className="absolute right-2 top-1/2 -translate-y-1/2 bg-white p-2 rounded-full shadow hidden md:block"
          >
            ▶
          </button>
        </div>
      </Section> */}



      {promoOffers.length > 0 && (
        <PromoStrip
          size="lg"
          showBadge={false}
          offers={promoOffers}
          onApplyCode={handleApplyCodeFromBanner}
          pauseOnHover={false}
        />
      )}

      {showCouponModal && couponToShow && (
        <CouponModal
          coupon={couponToShow}
          isLoggedIn={isLoggedIn}
          onClose={() => {
            setShowCouponModal(false);
            setCouponToShow(null);
          }}
          dontShowKey={COUPON_MODAL_KEY}
          persistDontShow={true}
        />
      )}


      <Section title="What our users say">
        <div className="flex snap-x snap-mandatory gap-4 overflow-x-auto px-3 pb-2 font-sans no-scrollbar sm:px-4 md:grid md:grid-cols-3 md:gap-5 md:overflow-visible md:px-0 md:pb-0">
          {testimonials.map((t, idx) => (
            <div
              key={idx}
              className="w-[80%] shrink-0 snap-start sm:w-[55%] md:w-auto md:shrink"
            >
              <TestimonialCard t={t} />
            </div>
          ))}
        </div>
      </Section>

      <AppCTA />
      <Newsletter />
    </>
  );
}




// // src/pages/HomePage.jsx
// import React, { useEffect, useState } from "react";
// import Hero from "../components/Hero.jsx";
// import CategoryBar from "../components/CategoryBar.jsx";
// import Section from "../components/Section.jsx";
// import EventCard from "../components/EventCard.jsx";
// import FeaturedBig from "../components/FeaturedBig.jsx";
// import CityChip from "../components/CityChip.jsx";
// import TestimonialCard from "../components/TestimonialCard.jsx";
// import AppCTA from "../components/AppCTA.jsx";
// import OrganizerCTA from "../components/OrganizerCTA.jsx";
// import Newsletter from "../components/Newsletter.jsx";
// import { Link } from "react-router-dom";

// import { apiFeatured, apiTrending, apiWeekend } from "../api/events.js";
// // cities/testimonials ko alag file me rakho:
// // import { cities, testimonials } from "../api/static.js";
// import { cities, testimonials } from "../api/events.js";
// import FeaturedCarousel from "../components/FeaturedCarousel.jsx";

// export default function HomePage() {
//   const [featured, setFeatured] = useState([]);
//   const [trending, setTrending] = useState([]);
//   const [weekend, setWeekend] = useState([]);
//   const [city, setCity] = useState("Mumbai");

//   useEffect(() => {
//     apiFeatured(3).then(setFeatured);
//     apiWeekend().then(setWeekend);
//   }, []);



//   useEffect(() => { apiTrending(city, 4).then(setTrending); }, [city]);

//   useEffect(() => {
//     const handler = (e) => setCity(e.detail);
//     window.addEventListener("city:change", handler);
//     return () => window.removeEventListener("city:change", handler);
//   }, []);

//   const highlight = featured[0] ? {
//     title: featured[0].title,
//     subtitle: featured[0].location,
//     location: featured[0].location,
//     date: featured[0].date,
//     price: featured[0].price,
//     image: featured[0].image,
//     badge: featured[0].tag || "Just Announced"
//   } : {
//     title: "Featured Event",
//     subtitle: "",
//     location: "",
//     date: "",
//     price: "",
//     image: "",
//     badge: "Just Announced"
//   };

//   return (
//     <>
//       <Hero />
//       <CategoryBar />

//       <Section id="featured" title="Featured Events" bgClass="bg-[#fff8e1]" fullBleed
//         right={<Link to="/featured" className="text-sm text-brand-700 hover:underline">View More</Link>}>
//         <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
//           {featured.map((e) => <EventCard key={e.id} item={e} />)}
//         </div>
//       </Section>

//       <Section id="trending" title={`Trending in ${city}`}
//         right={
//           <div className="flex items-center gap-3">
//             <div className="flex items-center gap-2">
//               <span className="text-sm text-brand-700">Change City</span>
//               <select className="border rounded-md px-3 py-2 text-sm" value={city} onChange={(e) => setCity(e.target.value)}>
//                 {cities.map((c) => (<option key={c.name} value={c.name}>{c.name}</option>))}
//               </select>
//             </div>
//             <Link to={`/trending?city=${encodeURIComponent(city)}`} className="text-sm text-brand-700 hover:underline">View All</Link>
//           </div>
//         }>
//         <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
//           {trending.map((e) => (
//             <EventCard key={e.id} item={e} compact showBookButton={false} bodyBg="yellow" />
//           ))}
//         </div>
//       </Section>

//       <Section id="weekend" title="This Weekend" bgClass="bg-[#fff8e1]" fullBleed
//         right={<Link to="/weekend" className="text-sm text-brand-700 hover:underline">View All</Link>}>
//         <div className="-mx-4">
//           <div className="no-scrollbar flex gap-4 overflow-x-auto scroll-smooth snap-x snap-mandatory px-4 pb-2 md:pr-12 lg:pr-16 overscroll-x-contain">
//             {weekend.map((e, i) => (
//               <div key={(e.id || i)} className="shrink-0 snap-start w-[calc(100%_-_48px)] sm:w-[calc(100%_-_48px)] md:w-[calc((100%_-_48px_-_16px)/2)] lg:w-[calc((100%_-_64px_-_32px)/4)]">
//                 <EventCard item={e} compact showBookButton={false} />
//               </div>
//             ))}
//           </div>
//         </div>
//       </Section>

//       <Section title="Featured Event" fullBleed>
//         <FeaturedCarousel />
//       </Section>
//       <Section id="cities" title="Explore Events by City">
//         <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
//           {cities.map((c) => <CityChip key={c.name} c={c} />)}
//         </div>
//       </Section>

//       <Section title="What Our Users Say">
//         <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
//           {testimonials.map((t, idx) => <TestimonialCard key={idx} t={t} />)}
//         </div>
//       </Section>

//       <AppCTA />
//       <OrganizerCTA />
//       <Newsletter />
//     </>
//   );
// }




// // import React, { useEffect, useState } from "react";
// // import Hero from "../components/Hero.jsx";
// // import CategoryBar from "../components/CategoryBar.jsx";
// // import Section from "../components/Section.jsx";
// // import EventCard from "../components/EventCard.jsx";
// // import FeaturedBig from "../components/FeaturedBig.jsx";
// // import CityChip from "../components/CityChip.jsx";
// // import TestimonialCard from "../components/TestimonialCard.jsx";
// // import AppCTA from "../components/AppCTA.jsx";
// // import OrganizerCTA from "../components/OrganizerCTA.jsx";
// // import Newsletter from "../components/Newsletter.jsx";
// // import { Link } from "react-router-dom";
// // import { apiFeatured, apiTrending, apiWeekend, cities, testimonials } from "../api/events.js";

// // export default function HomePage() {
// //     const [featured, setFeatured] = useState([]);
// //     const [trending, setTrending] = useState([]);
// //     const [weekend, setWeekend] = useState([]);
// //     const [city, setCity] = useState("Mumbai");

// //     useEffect(() => { apiFeatured().then(setFeatured); apiWeekend().then(setWeekend); }, []);
// //     useEffect(() => { apiTrending(city).then(setTrending); }, [city]);

// //     // HomePage.jsx
// //     useEffect(() => {
// //         const handler = (e) => setCity(e.detail);
// //         window.addEventListener("city:change", handler);
// //         return () => window.removeEventListener("city:change", handler);
// //     }, []);


// //     const highlight = {
// //         id: "4",
// //         title: "A.R. Rahman Live: One Heart Tour",
// //         subtitle: "Jawaharlal Nehru Stadium, Delhi",
// //         location: "Jawaharlal Nehru Stadium, Delhi",
// //         date: "Tue, 5 Dec 2023 · 7:00 PM Onwards",
// //         dateBlock: { date: "5 Dec 2023", time: "7:00 PM Onwards" },

// //         price: 1999,
// //         image:
// //             "https://images.unsplash.com/photo-1492684223066-81342ee5ff30?q=80&w=1600&auto=format&fit=crop",

// //         duration: "3 hours",
// //         languages: "Hindi, Tamil, English",
// //         age: "Age 5+",

// //         // Ticket boxes (Silver/Gold)
// //         tickets: [
// //             { key: "silver", name: "Silver", desc: "Regular seating, limited view", price: 1999, status: "few" },
// //             { key: "gold", name: "Gold", desc: "Premium seating, clear view", price: 3499, status: "available" },
// //         ],

// //         category: "Music",
// //     };


// //     return (
// //         <>
// //             <Hero />
// //             <CategoryBar />

// //             <Section id="featured" title="Featured Events" bgClass="bg-[#fff8e1]" fullBleed
// //                 right={<Link to="/featured" className="text-sm text-brand-700 hover:underline">View More</Link>}>
// //                 <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
// //                     {console.log('sasasasas',featured )}
// //                     {featured.map((e) => <EventCard key={e.id} item={e} />)}
// //                 </div>
// //             </Section>

// //             <Section id="trending" title={`Trending in ${city}`}
// //                 right={
// //                     <div className="flex items-center gap-3">
// //                         <div className="flex items-center gap-2">
// //                             <span className="text-sm text-brand-700">Change City</span>
// //                             <select className="border rounded-md px-3 py-2 text-sm" value={city} onChange={(e) => setCity(e.target.value)}>
// //                                 {cities.map((c) => (<option key={c.name} value={c.name}>{c.name}</option>))}
// //                             </select>
// //                         </div>
// //                         <Link to={`/trending?city=${encodeURIComponent(city)}`} className="text-sm text-brand-700 hover:underline">View All</Link>
// //                     </div>
// //                 }>
// //                 <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
// //                     {/* {trending.map((e) => <EventCard key={e.id} item={e} compact showBookButton={false} />)} */}
// //                     {trending.map((e) => (
// //                         <EventCard key={e.id} item={e} compact showBookButton={false} bodyBg="yellow" />
// //                     ))}
// //                 </div>
// //             </Section>

// //             <Section id="weekend" title="This Weekend" bgClass="bg-[#fff8e1]" fullBleed
// //                 right={<Link to="/weekend" className="text-sm text-brand-700 hover:underline">View All</Link>}>
// //                 <div className="-mx-4">
// //                     <div className="no-scrollbar flex gap-4 overflow-x-auto scroll-smooth snap-x snap-mandatory px-4 pb-2 md:pr-12 lg:pr-16 overscroll-x-contain">
// //                         {weekend.map((e, i) => (
// //                             <div key={e.id + i} className="shrink-0 snap-start w-[calc(100%_-_48px)] sm:w-[calc(100%_-_48px)] md:w-[calc((100%_-_48px_-_16px)/2)] lg:w-[calc((100%_-_64px_-_32px)/4)]">
// //                                 <EventCard item={e} compact showBookButton={false} />
// //                             </div>
// //                         ))}
// //                     </div>
// //                 </div>
// //             </Section>

// //             <Section title="Featured Event" fullBleed>
// //                 <FeaturedBig item={highlight} />
// //             </Section>

// //             {/* <Section id="cities" title="Explore Events by City">
// //         <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
// //           {cities.map((c) => <CityChip key={c.name} c={c} />)}
// //         </div>
// //       </Section> */}

// //             <Section id="cities" title="Explore Events by City">
// //                 <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
// //                     {cities.map((c) => <CityChip key={c.name} c={c} />)}
// //                 </div>
// //             </Section>

// //             <Section title="What Our Users Say">
// //                 <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
// //                     {testimonials.map((t, idx) => <TestimonialCard key={idx} t={t} />)}
// //                 </div>
// //             </Section>

// //             <AppCTA />
// //             <OrganizerCTA />
// //             <Newsletter />
// //         </>
// //     );
// // }
